import type { Metrics, ProductPerformance, Alert } from '@/types/client';

// Mock data for Karaskin Paris
export const MOCK_METRICS: Metrics = {
  revenue: 87500,
  orders: 892,
  returnsRate: 0.0340,
  conversionRate: 0.0420,
  buyBoxShare: 0.8340,
  cancellationsRate: 0.0180,
  csat: 4.2,
  ads: {
    spend: 12500,
    salesAttributed: 28900,
    acos: 0.2145,
    roas: 4.32,
  },
  delivery: {
    onTimeRate: 0.9220,
    lateRate: 0.0780,
    avgShipTimeDays: 1.8,
  },
  fees: {
    total: 15750,
    referral: 8750,
    fba: 4500,
    adsFees: 2500,
  },
  amazonFees: {
    total: 15750,
    referral: 8750,
    fba: 4500,
    adsFees: 2500,
  },
  buyBox: {
    share: 0.8340,
  },
  inventory: {
    inStockSkus: 187,
    oosSkus: 8,
    coverageDays: 23,
  },
};

export const MOCK_PRODUCTS: ProductPerformance[] = [
  {
    asin: 'B09XYZ1234',
    title: 'Shampoing Réparateur Karaskin 500ml',
    revenue: 18750,
    orders: 187,
    conversion: 0.048,
    returns: 0.025,
    buyBoxShare: 0.92,
    stock: 245,
    margin: 0.28,
  },
  {
    asin: 'B09ABC5678',
    title: 'Masque Capillaire Nutrition Intense',
    revenue: 15200,
    orders: 152,
    conversion: 0.052,
    returns: 0.018,
    buyBoxShare: 0.87,
    stock: 189,
    margin: 0.32,
  },
  {
    asin: 'B09DEF9012',
    title: 'Sérum Anti-Chute Premium',
    revenue: 22100,
    orders: 221,
    conversion: 0.039,
    returns: 0.042,
    buyBoxShare: 0.78,
    stock: 67,
    margin: 0.41,
  },
  {
    asin: 'B09GHI3456',
    title: 'Huile Capillaire Réparatrice Bio',
    revenue: 8900,
    orders: 89,
    conversion: 0.044,
    returns: 0.012,
    buyBoxShare: 0.95,
    stock: 312,
    margin: 0.35,
  },
  {
    asin: 'B09JKL7890',
    title: 'Kit Complet Soin Cheveux Abîmés',
    revenue: 12800,
    orders: 128,
    conversion: 0.029,
    returns: 0.051,
    buyBoxShare: 0.82,
    stock: 45,
    margin: 0.22,
  },
];

export const MOCK_ALERTS: Alert[] = [
  {
    id: '1',
    type: 'buybox_loss',
    severity: 'high',
    title: 'Perte Buy Box - Sérum Anti-Chute',
    description: 'ASIN B09DEF9012 a perdu la Buy Box 22% du temps cette semaine. Prix concurrent 5% inférieur.',
    actionUrl: '/dashboard/karaskin-paris/produits/B09DEF9012',
    created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '2',
    type: 'stock_low',
    severity: 'critical',
    title: 'Stock critique - Kit Complet',
    description: 'ASIN B09JKL7890 : 45 unités restantes, rupture prévue dans 8 jours.',
    actionUrl: '/dashboard/karaskin-paris/stocks/reappro',
    created_at: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '3',
    type: 'acos_high',
    severity: 'medium',
    title: 'ACoS élevé - Campagne Shampoing',
    description: 'Campagne "Shampoing Réparateur" : ACoS 28% (seuil: 25%). Budget journalier épuisé à 14h.',
    actionUrl: '/dashboard/karaskin-paris/publicite/campagnes',
    created_at: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '4',
    type: 'returns_high',
    severity: 'medium',
    title: 'Taux retour anormal - Kit Complet',
    description: 'ASIN B09JKL7890 : 5.1% retours vs 2.8% habituel. Motifs: "packaging endommagé".',
    actionUrl: '/dashboard/karaskin-paris/qualite/retours',
    created_at: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
  },
];

// Generate time series data for charts
export function generateTimeSeriesData(days: number = 30): Array<{
  date: string;
  revenue: number;
  orders: number;
  conversion: number;
  buyBoxShare: number;
  acos: number;
  roas: number;
}> {
  const data = [];
  const baseDate = new Date();
  
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(baseDate);
    date.setDate(date.getDate() - i);
    
    const trend = 1 + (Math.sin((days - i) / 7) * 0.1); // Weekly trend
    const randomFactor = 0.8 + Math.random() * 0.4; // ±20% random variation
    
    data.push({
      date: date.toISOString().split('T')[0],
      revenue: Math.round((MOCK_METRICS.revenue / days) * trend * randomFactor),
      orders: Math.round((MOCK_METRICS.orders / days) * trend * randomFactor),
      conversion: parseFloat((MOCK_METRICS.conversionRate * trend * randomFactor).toFixed(4)),
      buyBoxShare: parseFloat((MOCK_METRICS.buyBox.share * trend * randomFactor).toFixed(4)),
      acos: parseFloat((MOCK_METRICS.ads.acos * (2 - trend) * randomFactor).toFixed(4)),
      roas: parseFloat((MOCK_METRICS.ads.roas * trend * randomFactor).toFixed(2)),
    });
  }
  
  return data;
}

// Generate marketplace breakdown
export const MOCK_MARKETPLACE_DATA = [
  { marketplace: 'amazon.fr', revenue: 42000, orders: 420, share: 0.48 },
  { marketplace: 'amazon.de', revenue: 28000, orders: 280, share: 0.32 },
  { marketplace: 'amazon.it', revenue: 12250, orders: 122, share: 0.14 },
  { marketplace: 'amazon.es', revenue: 5250, orders: 70, share: 0.06 },
];

// Campaign performance data
export const MOCK_CAMPAIGNS = [
  {
    id: 'camp_001',
    name: 'Shampoing Réparateur - Exact',
    type: 'sp',
    status: 'enabled',
    spend: 4200,
    sales: 18900,
    acos: 0.22,
    roas: 4.5,
    impressions: 285000,
    clicks: 1420,
    ctr: 0.0050,
  },
  {
    id: 'camp_002',
    name: 'Masque Nutrition - Broad',
    type: 'sp',
    status: 'enabled',
    spend: 3100,
    sales: 12400,
    acos: 0.25,
    roas: 4.0,
    impressions: 195000,
    clicks: 975,
    ctr: 0.0050,
  },
  {
    id: 'camp_003',
    name: 'Sérum Anti-Chute - Brand Defense',
    type: 'sb',
    status: 'enabled',
    spend: 2800,
    sales: 15200,
    acos: 0.18,
    roas: 5.4,
    impressions: 420000,
    clicks: 1680,
    ctr: 0.0040,
  },
];